/*
 * Назначение: страница «Настройки проекта».
 * Функционал: семь подвкладок —
 *   общие, внешний вид (виджет), каналы (сайт/Telegram/MAX), модели,
 *   промпт и ограничения, менеджеры и уведомления, напоминания.
 * Каждая вкладка живёт в отдельном модуле (settings/*.tsx) и имеет
 * свой URL: /settings, /settings/appearance, /settings/channels, /settings/models,
 * /settings/prompt, /settings/managers, /settings/reminders.
 */

import { AlertCircle, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

import { AppearanceTab } from "./settings/appearance-tab";
import { ChannelsTab } from "./settings/channels-tab";
import { GeneralTab } from "./settings/general-tab";
import { ManagersTab } from "./settings/managers-tab";
import { GenerationParams } from "./settings/generation-params";
import { ModelsTab } from "./settings/models-tab";
import { PromptTab } from "./settings/prompt-tab";
import { RemindersTab } from "./settings/reminders-tab";
import { useProjectDetails } from "./settings/shared";

const TAB_BY_PATH: Record<string, string> = {
  appearance: "appearance",
  channels: "channels",
  models: "models",
  prompt: "prompt",
  managers: "managers",
  reminders: "reminders",
};

export function SettingsPage() {
  const navigate = useNavigate();
  const params = useParams<{ tab?: string }>();
  const tab = (params.tab && TAB_BY_PATH[params.tab]) || "general";

  const setTab = (next: string) => {
    if (next === "general") {
      navigate("/settings", { replace: false });
    } else {
      navigate(`/settings/${next}`, { replace: false });
    }
  };

  const { data: project, isLoading, isError, refetch } = useProjectDetails();

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Загружаем настройки…
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-8 flex flex-col items-center gap-3 text-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p>Не удалось загрузить настройки</p>
            <Button variant="outline" onClick={() => refetch()}>
              Попробовать снова
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Настройки</h2>
        <p className="text-sm text-muted-foreground">
          Параметры проекта «{project.name}». Изменения применяются мгновенно.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-7">
          <TabsTrigger value="general">Общие</TabsTrigger>
          <TabsTrigger value="appearance">Внешний вид</TabsTrigger>
          <TabsTrigger value="channels">Каналы</TabsTrigger>
          <TabsTrigger value="models">Модели</TabsTrigger>
          <TabsTrigger value="prompt">Промпт</TabsTrigger>
          <TabsTrigger value="managers">Менеджеры</TabsTrigger>
          <TabsTrigger value="reminders">Напоминания</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <GeneralTab project={project} />
        </TabsContent>
        <TabsContent value="appearance" className="space-y-6">
          <AppearanceTab project={project} />
        </TabsContent>
        <TabsContent value="channels" className="space-y-6">
          <ChannelsTab project={project} />
        </TabsContent>
        <TabsContent value="models" className="space-y-6">
          <ModelsTab project={project} />
          <GenerationParams project={project} />
        </TabsContent>
        <TabsContent value="prompt" className="space-y-6">
          <PromptTab project={project} />
        </TabsContent>
        <TabsContent value="managers" className="space-y-6">
          <ManagersTab project={project} />
        </TabsContent>
        <TabsContent value="reminders" className="space-y-6">
          <RemindersTab project={project} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
